import { useEffect } from "react";

/**
 * @astrana/ui defines its CSS variables AND its utility classes scoped under
 * `.astrana-ui` (e.g. `.astrana-ui .bg-card{…}`). The lib's dialogs/popovers
 * are Radix-portaled to `<body>` — outside the `.astrana-ui` subtree that
 * MemberProfile renders — so their surfaces render unstyled/transparent.
 *
 * Rather than class `<body>` (which leaks the lib's typography/palette onto the
 * whole app), we tag ONLY the Radix portal wrapper `<div>` that Radix inserts
 * directly under `<body>`. That gives the portaled content an `.astrana-ui`
 * ancestor — scoping just the dialog/popover subtree, leaving app chrome
 * untouched. We tag the wrapper (not the content node itself, and we never
 * reparent) so React's portal reconciliation is unaffected.
 */
/**
 * `role="dialog"`/`role="alertdialog"` alone is not a reliable "this came from
 * @astrana/ui" signal — Radix's own Portal wrapper carries no distinguishing
 * marker of its own (see the header above), so this has to match on the
 * dialog content's ARIA role instead. That over-matches host-authored
 * dialogs that use the same WAI-ARIA role for their own markup and were never
 * rendered by @astrana/ui at all (e.g. member-360's `CreateClaimGate`), which
 * then get wrongly tagged `.astrana-ui` and inherit the LIBRARY's own
 * CSS-variable palette/font instead of the host's `:root`/`.dark` tokens —
 * AM-5824's "completely distorted" Select Provider popup: `.dark .astrana-ui`
 * repaints the whole subtree with the library's pure-gray dark scheme at
 * higher specificity than the host's own `.dark` rule. (The font half of that
 * symptom is gone — index.css re-points `.astrana-ui`'s `font-family` at
 * `--font-body`, so a mis-tagged subtree no longer flips to the lib's Inter.) A host dialog that deliberately renders with
 * the host's own tokens (no @astrana/ui component inside it) opts out with
 * `data-astrana-ui-exempt` on its `role="dialog"` element.
 */
const PORTAL_CONTENT_SELECTOR =
  '[role="dialog"]:not([data-astrana-ui-exempt]),[role="alertdialog"]:not([data-astrana-ui-exempt]),[data-radix-popper-content-wrapper]';

function scopeBodyLevelAncestor(el: Element): void {
  // Walk up to the direct child of <body> (the Radix portal wrapper), then add
  // the scope class there so the content node and its descendants resolve the
  // lib's `.astrana-ui …` rules.
  let node: Element = el;
  while (node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  if (node instanceof HTMLElement && !node.classList.contains("astrana-ui")) {
    node.classList.add("astrana-ui");
  }
}

function scan(root: ParentNode): void {
  root
    .querySelectorAll?.(PORTAL_CONTENT_SELECTOR)
    .forEach(scopeBodyLevelAncestor);
}

export function useAstranaPortalScope(): void {
  useEffect(() => {
    scan(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches?.(PORTAL_CONTENT_SELECTOR)) {
            scopeBodyLevelAncestor(n);
          }
          scan(n);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
